import { doc, setDoc } from 'firebase/firestore';
import type {
  RealEstateProperty,
  RealEstatePropertyType,
  RealEstateProjectPhase,
  RealEstateProjectTask,
} from '../types';
import { newId } from '../utils/id';
import { normalizeWebsiteUrl } from '../utils/favicon';
import { db } from './firebase';
import { COLLECTION_REAL_ESTATE_ITEMS, ownedItemDocId } from './firestorePaths';

export type RealEstatePropertyInput = {
  id?: string;
  name: string;
  propertyType: RealEstatePropertyType;
  address?: string;
  websiteUrl?: string;
  currentValue: number;
  purchasePrice?: number;
  purchaseDate?: string;
  mortgageBalance?: number;
  monthlyRent?: number;
  appreciationRate?: number;
  notes?: string;
  projectPhases?: RealEstateProjectPhase[];
  projectTasks?: RealEstateProjectTask[];
  /** When updating, pass fields from app state — Firestore denies get on non-existent owned docs. */
  existing?: Pick<RealEstateProperty, 'createdAt' | 'projectPhases' | 'projectTasks'>;
};

function stripUndefinedFieldsDeep(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value.map(stripUndefinedFieldsDeep);
  }
  if (typeof value === 'object' && value !== null) {
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v !== undefined) {
        next[k] = stripUndefinedFieldsDeep(v);
      }
    }
    return next;
  }
  return value;
}

function stripUndefinedFields(obj: Record<string, unknown>): Record<string, unknown> {
  return stripUndefinedFieldsDeep(obj) as Record<string, unknown>;
}

export async function saveRealEstateProperty(
  userId: string,
  input: RealEstatePropertyInput
): Promise<RealEstateProperty> {
  const name = input.name.trim();
  if (!name) {
    throw new Error('Property name is required.');
  }

  const currentValue = Number(input.currentValue);
  if (!Number.isFinite(currentValue)) {
    throw new Error('Enter a valid market value.');
  }

  const now = new Date().toISOString();
  const id = input.id?.trim() || newId();
  const docRef = doc(db, COLLECTION_REAL_ESTATE_ITEMS, ownedItemDocId(userId, id));

  const property: RealEstateProperty = {
    id,
    userId,
    name,
    propertyType: input.propertyType,
    address: input.address?.trim() || undefined,
    websiteUrl: normalizeWebsiteUrl(input.websiteUrl),
    currentValue,
    purchasePrice:
      typeof input.purchasePrice === 'number' && Number.isFinite(input.purchasePrice)
        ? input.purchasePrice
        : undefined,
    purchaseDate: input.purchaseDate?.trim() || undefined,
    mortgageBalance:
      typeof input.mortgageBalance === 'number' && Number.isFinite(input.mortgageBalance)
        ? input.mortgageBalance
        : undefined,
    monthlyRent:
      typeof input.monthlyRent === 'number' && Number.isFinite(input.monthlyRent)
        ? input.monthlyRent
        : undefined,
    appreciationRate:
      typeof input.appreciationRate === 'number' && Number.isFinite(input.appreciationRate)
        ? input.appreciationRate
        : undefined,
    notes: input.notes?.trim() || undefined,
    projectPhases: input.projectPhases ?? input.existing?.projectPhases ?? undefined,
    projectTasks: input.projectTasks ?? input.existing?.projectTasks ?? undefined,
    valueUpdatedAt: now,
    createdAt: input.existing?.createdAt ?? now,
    updatedAt: now,
  };

  await setDoc(docRef, stripUndefinedFields({ ...property }), { merge: true });

  return property;
}

export async function deleteRealEstateProperty(userId: string, id: string): Promise<void> {
  const { deleteDoc } = await import('firebase/firestore');
  const docRef = doc(db, COLLECTION_REAL_ESTATE_ITEMS, ownedItemDocId(userId, id));
  await deleteDoc(docRef);
}
